import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'action');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = currentCategory?.name || '全部';

  const filteredMovies =
    activeSlug === 'all'
      ? movies
      : movies.filter((m) => m.category === activeSlug);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="pt-20 pb-6 bg-background-100 border-b border-background-300/40">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: categoryName },
                ]}
              />
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading mt-2">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {filteredMovies.length} 部电影
              </p>
            </div>
          </div>
        </div>

        <div className="py-8">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8">
                <aside className="lg:w-56 shrink-0">
                  <div className="sticky top-24">
                    <h3 className="text-sm font-semibold text-foreground-300 mb-3 font-heading">
                      电影分类
                    </h3>
                    <nav className="space-y-1">
                      {categories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/category/${cat.slug}`}
                          onClick={() => setActiveSlug(cat.slug)}
                          className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                            activeSlug === cat.slug
                              ? 'bg-primary-500/10 text-primary-400 font-medium'
                              : 'text-foreground-500 hover:text-foreground-200 hover:bg-background-200'
                          }`}
                        >
                          <span>{cat.name}</span>
                          <span className="text-xs text-foreground-600">
                            {cat.count}
                          </span>
                        </Link>
                      ))}
                    </nav>
                  </div>
                </aside>

                <div className="flex-1 min-w-0">
                  {filteredMovies.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5">
                      {filteredMovies.map((movie) => {
                        const cat = categories.find(
                          (c) => c.slug === movie.category
                        );
                        return (
                          <MovieCard
                            key={movie.id}
                            id={movie.id}
                            title={movie.title}
                            year={movie.year}
                            rating={movie.rating}
                            image={movie.image}
                            quality={movie.quality}
                            categoryName={cat?.name}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <div className="w-16 h-16 flex items-center justify-center mb-4">
                        <i className="ri-movie-2-line text-4xl text-foreground-700"></i>
                      </div>
                      <p className="text-foreground-600 text-sm">
                        该分类暂无电影
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${categoryName} - 6080影视`,
            description: `6080影视${categoryName}分类，提供高清免费在线观看服务。`,
            url: `https://yunlv8888.cn/category/${activeSlug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://yunlv8888.cn/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: categoryName,
                },
              ],
            },
          }),
        }}
      />
    </div>
  );
}